// /app/frontend/src/pages/seo/BuildingPolygamousFamilyPage.jsx
// Focus: VALUES, FAITH-AWARE FOUNDATIONS, the philosophy of a family.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function BuildingPolygamousFamilyPage() {
  const path = '/building-a-polygamous-family';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Building A Polygamous Family', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Building A Polygamous Family"
        description="Discover the principles of communication, trust, parenting, and shared values that support healthy family-building."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Building A Strong Family Foundation"
        sub="A polygamous family is not built on chemistry alone. It is built on values — and the willingness to keep choosing them when the work gets hard."
        body="This guide is for couples and singles laying the foundation for a polygamous family, whether rooted in faith, culture, or simply a deep belief that family can take more than one shape."
        primaryCta={{ label: 'Create Your Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'How To Find A Sister Wife', to: '/how-to-find-a-sister-wife' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12">
          <p className="text-lg text-slate-700 leading-relaxed">
            Polygamy is not a relationship style — it is a family structure. That distinction matters more than almost anything else covered in this guide. A relationship can drift. A family is a commitment that does not. The healthiest polygamous families begin with a clear-eyed agreement about what they are building, why, and what they will hold sacred even when life is difficult.
          </p>
        </section>

        {/* The five foundational values */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            Five Values That Carry A Polygamous Family
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🛡️"
              accent="rose"
              title="Honesty Without Cruelty"
              body="Tell the truth, even when it stings — but tell it with care. Plural families cannot survive on managed information; the cost of a single lie is multiplied by the number of people it touches."
            />
            <FeatureCard
              icon="🤝"
              accent="emerald"
              title="Equity Over Sameness"
              body="No two wives have identical needs. The goal is not equal in form but equitable in care — making sure no one feels less loved, less heard, or less central than the others."
            />
            <FeatureCard
              icon="🧭"
              accent="sky"
              title="Shared Vision"
              body="Every adult in the family can articulate the same answer to: where are we trying to go? Disagreement on the destination is the slow killer of plural families."
            />
            <FeatureCard
              icon="🪞"
              accent="violet"
              title="Self-Awareness"
              body="Polygamy stress-tests every adult in it. Members who do their own emotional work — therapy, reflection, accountability — keep the family healthier than those who outsource that work to their partners."
            />
            <FeatureCard
              icon="🌿"
              accent="amber"
              title="Patience"
              body="Plural family-building runs on a multi-year clock, not a multi-month one. Hurry is the most consistent symptom of an unhealthy family in formation."
            />
          </div>
        </section>

        {/* Trust */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            Trust Is The Currency
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              In monogamy, trust between two people can absorb a lot. In a plural family, trust is a network — and every untrusted thread weakens the whole. The work of building trust never ends, but it accelerates dramatically when adults practice three habits:
            </p>
            <ul className="list-disc list-inside text-slate-700 ml-2 space-y-1">
              <li>Saying what they are going to do, and doing it.</li>
              <li>Naming hard feelings before they become resentments.</li>
              <li>Refusing to triangulate — bringing concerns directly to the person involved.</li>
            </ul>
            <p>
              These sound simple. They are not. Plural families that practice them consistently look unrecognizable from the ones that do not, within a single year.
            </p>
          </div>
        </section>

        {/* Faith-aware section */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Faith, Culture, And Family
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              For many families, the foundation of polygamy is spiritual — rooted in Islamic, African traditional, Mormon-adjacent, or other faith and cultural frameworks. For others, it is secular — an intentional choice about how family can be built. Both paths are legitimate. Both produce healthy families when the underlying work is done.
            </p>
            <p>
              Where families struggle is when the faith or cultural framing is used to skip the work. A spiritual conviction that polygamy is right does not, by itself, build a healthy family. The conviction is the why. The communication, the trust, the equity, the patience — those are the how.
            </p>
            <p>
              UnityLove welcomes members from every framing. Profile prompts and community spaces support both faith-rooted and secular polygamous family-building.
            </p>
          </div>
        </section>

        {/* Parenting principles */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            Parenting Together
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Children raised in healthy plural families often describe a kind of abundance — more adults who love them, more cousins, more cultural continuity. The cost is coordination. Every plural family raising children needs explicit alignment on discipline, education, language about the family used in public, and the role each adult plays.
            </p>
            <p>
              Children should never become the means by which adults compete or communicate. When tension exists between adults, it gets resolved between adults — not through the kids.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Continue Reading
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/sister-wives-and-poly-family-building" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Sister Wives & Poly Family Building</p>
              <p className="text-sm text-slate-600 mt-1">Structures and dynamics of plural families.</p>
            </a>
            <a href="/poly-family-planning" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Family Planning</p>
              <p className="text-sm text-slate-600 mt-1">Practical planning for plural families.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
